import React, { useMemo, useState } from 'react'
import * as THREE from 'three'
import { Text } from '@react-three/drei'
import { useThree } from '@react-three/fiber'

const BRAND_COLORS = {
  darkest: '#01303f',  
  dark: '#02577a',     
  medium: '#02a9f7',   
  light: '#89d6fb',    
  lightest: '#d4f0fc'  
};

const INVERTED_BRAND_COLORS = {
  darkest: '#d4f0fc',  
  dark: '#89d6fb',    
  medium: '#02a9f7',
  light: '#02577a',
  lightest: '#01303f'
};

interface TabletProps {
  position?: [number, number, number]
  width?: number
  thickness?: number
  screenDepth?: number
  color?: string
  hoverColor?: string
  label?: string
  children?: React.ReactNode
  index?: number
  hoveredIndex?: number | null
  onHover?: (index: number | null) => void
}

const Tablet: React.FC<TabletProps> = ({
  position = [0, 0, 0],
  width = 2,
  thickness = 0.2,
  screenDepth = 1,
  color = BRAND_COLORS.light,
  hoverColor = INVERTED_BRAND_COLORS.light,
  label = '',
  children,
  index = -1,
  hoveredIndex = null,
  onHover = () => {},
}) => {
  const isHovered = index === hoveredIndex;
  
  const currentColor = isHovered ? hoverColor : color;
  
  const glowIntensity = isHovered ? 0.5 : 0;

  const geometry = useMemo(
    () => new THREE.BoxGeometry(width, thickness, screenDepth),
    [width, thickness, screenDepth]
  )

  const materials = useMemo(
    () => [
      new THREE.MeshBasicMaterial({ color: currentColor, side: THREE.DoubleSide }),
      new THREE.MeshBasicMaterial({ color: currentColor, side: THREE.DoubleSide }),
      new THREE.MeshBasicMaterial({ color: currentColor, side: THREE.DoubleSide }),
      new THREE.MeshBasicMaterial({ color: currentColor, side: THREE.DoubleSide }),
      new THREE.MeshBasicMaterial({ color: currentColor, side: THREE.DoubleSide }),
      new THREE.MeshBasicMaterial({ color: currentColor, side: THREE.DoubleSide }),
    ],
    [currentColor]
  )

  const adjustedPosition: [number, number, number] = [
    position[0],
    position[1] + thickness / 2,
    position[2],
  ]

  const solidEdgesGeometry = useMemo(() => new THREE.EdgesGeometry(geometry), [geometry])

  const borderOffset = 0.05
  const topBorderGeometry = useMemo(() => {
    const x0 = -width / 2 + borderOffset
    const x1 = width / 2 - borderOffset
    const z0 = -screenDepth / 2 + borderOffset
    const z1 = screenDepth / 2 - borderOffset
    const vertices = new Float32Array([
      x0, thickness / 2, z0,
      x1, thickness / 2, z0,
      x1, thickness / 2, z1,
      x0, thickness / 2, z1,
      x0, thickness / 2, z0,
    ])
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(vertices, 3))
    const distances = new Float32Array(5)
    distances[0] = 0
    for (let i = 1; i < 5; i++) {
      const ax = vertices[(i - 1) * 3]
      const ay = vertices[(i - 1) * 3 + 1]
      const az = vertices[(i - 1) * 3 + 2]
      const bx = vertices[i * 3]
      const by = vertices[i * 3 + 1]
      const bz = vertices[i * 3 + 2]
      distances[i] = distances[i - 1] + Math.sqrt((bx - ax) ** 2 + (by - ay) ** 2 + (bz - az) ** 2)
    }
    geo.setAttribute('lineDistance', new THREE.BufferAttribute(distances, 1))
    return geo
  }, [width, thickness, screenDepth, borderOffset])

  const { gl } = useThree();

  return (
    <group>
      <mesh 
        geometry={geometry} 
        material={materials} 
        position={adjustedPosition}
        onPointerOver={(e) => {
          e.stopPropagation();
          onHover(index);
          gl.domElement.style.cursor = 'pointer';
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          onHover(null);
          gl.domElement.style.cursor = 'auto';
        }}
      >
        {isHovered && (
          <pointLight 
            color={hoverColor} 
            intensity={glowIntensity} 
            distance={2} 
            position={[0, 0.5, 0]} 
          />
        )}
      </mesh>

      <lineSegments geometry={solidEdgesGeometry} position={adjustedPosition}>
        <lineBasicMaterial 
          color={isHovered ? "#ffffff" : "#ffffff"} 
          linewidth={1} 
          depthTest={true} 
        />
      </lineSegments>

      <lineLoop geometry={topBorderGeometry} position={adjustedPosition} renderOrder={10}>
        <lineDashedMaterial
          color={isHovered ? INVERTED_BRAND_COLORS.medium : BRAND_COLORS.medium}
          dashSize={0.1}
          gapSize={0.05}
          linewidth={1}
          depthTest={true}
          depthFunc={THREE.GreaterDepth}
        />
      </lineLoop>

      <group
        position={[
          adjustedPosition[0],
          adjustedPosition[1] + thickness / 2 - 0.01,
          adjustedPosition[2],
        ]}
        rotation={[-Math.PI / 2, 0, Math.PI]}
      >
        {children ? (
          <group scale={isHovered ? 1.1 : 1}>
            {children}
          </group>
        ) : (
          <Text fontSize={0.5} color={isHovered ? INVERTED_BRAND_COLORS.dark : BRAND_COLORS.dark} anchorX="center" anchorY="middle">
            {label}
          </Text>
        )}
      </group>
    </group>
  )
}

interface IPixelArtProps {
  pattern: number[][]
  pixelSize?: number
  color?: string
  isHovered?: boolean
}
const PixelArt: React.FC<IPixelArtProps> = ({ 
  pattern, 
  pixelSize = 0.1, 
  color = BRAND_COLORS.dark,
  isHovered = false
}) => {
  const displayColor = isHovered 
    ? (color === BRAND_COLORS.dark ? INVERTED_BRAND_COLORS.dark : INVERTED_BRAND_COLORS.dark)
    : color;
    
  return (
    <group>
      {pattern.map((row, rowIndex) =>
        row.map((val, colIndex) => {
          if (val) {
            return (
              <mesh
                key={`${rowIndex}-${colIndex}`}
                position={[
                  colIndex * pixelSize - (row.length * pixelSize) / 2,
                  -rowIndex * pixelSize + (pattern.length * pixelSize) / 2,
                  0,
                ]}
              >
                <boxGeometry args={[pixelSize, pixelSize, pixelSize]} />
                <meshBasicMaterial color={displayColor} />
              </mesh>
            )
          }
          return null
        })
      )}
    </group>
  )
}

const digitPatterns: { [key: string]: number[][] } = {
  '0': [
    [1, 1, 1],
    [1, 0, 1],
    [1, 1, 1],
  ],
  '1': [
    [1, 1, 0],
    [0, 1, 0],
    [0, 1, 0],
  ],
}

interface PixelBinaryNumberProps {
  binaryString: string
  pixelSize?: number
  digitSpacing?: number
  color?: string
  isHovered?: boolean
}
const PixelBinaryNumber: React.FC<PixelBinaryNumberProps> = ({
  binaryString,
  pixelSize = 0.05,
  digitSpacing = 0.01,
  color = BRAND_COLORS.dark,
  isHovered = false
}) => {
  return (
    <group>
      {binaryString.split('').map((digit, index) => {
        const pattern = digitPatterns[digit]
        if (!pattern) return null
        return (
          <group
            key={index}
            position={[index * (5 * pixelSize + digitSpacing) - 0.75, 0, 0]}
          >
            <PixelArt 
              pattern={pattern} 
              pixelSize={pixelSize} 
              color={color}
              isHovered={isHovered}
            />
          </group>
        )
      })}
    </group>
  )
}

interface PixelCandlestickChartProps {
  isHovered?: boolean
}

const PixelCandlestickChart: React.FC<PixelCandlestickChartProps> = ({ isHovered = false }) => {
  const candles = useMemo(() => {
    const arr: { open: number; close: number; high: number; low: number }[] = []
    for (let i = 0; i < 30; i++) {
      const open = Math.random() * 0.5 + 0.25
      const close = Math.random() * 0.5 + 0.25
      const high = Math.max(open, close) + Math.random() * 0.2
      const low = Math.min(open, close) - Math.random() * 0.2
      arr.push({ open, close, high, low })
    }
    return arr
  }, [])
  
  const chartWidth = 1.8
  const verticalOffset = -0.5

  return (
    <group>
      {candles.map((d, i) => {
        const x = i * (chartWidth / candles.length) - chartWidth / 2
        const bodyHeight = Math.abs(d.close - d.open)
        const bodyY = Math.min(d.open, d.close) + bodyHeight / 2 + verticalOffset
        
        const isUpCandle = d.close >= d.open
        const bodyColor = isHovered
          ? (isUpCandle ? INVERTED_BRAND_COLORS.medium : INVERTED_BRAND_COLORS.dark)
          : (isUpCandle ? BRAND_COLORS.medium : BRAND_COLORS.dark)
        
        return (
          <group key={i} position={[x, 0, 0]}>
            <mesh position={[0, bodyY, 0]}>
              <boxGeometry args={[0.03, bodyHeight, 0.05]} />
              <meshBasicMaterial color={bodyColor} />
            </mesh>
            <mesh position={[0, ((d.high + d.low) / 2) + verticalOffset, 0]}>
              <boxGeometry args={[0.01, d.high - d.low, 0.05]} />
              <meshBasicMaterial color={isHovered ? INVERTED_BRAND_COLORS.dark : BRAND_COLORS.dark} />
            </mesh>
          </group>
        )
      })}
    </group>
  )
}

   interface CustomMagnifyingGlassProps {
    isHovered?: boolean
  }
  
  const CustomMagnifyingGlass: React.FC<CustomMagnifyingGlassProps> = ({ isHovered = false }) => {
    const offsetX = 0.4, offsetY = 0.3
    const squareSize = 0.2
    
    const lineGeometry = useMemo(() => {
      const positions: [number, number, number][] = [
        [-offsetX, offsetY, 0],
        [offsetX, offsetY, 0],
        [offsetX, -offsetY, 0],
        [-offsetX, -offsetY, 0],
      ]
      
      const linePositions = new Float32Array([
        ...positions[0],
        ...positions[1],
        ...positions[2],
        ...positions[3],
        ...positions[0],
      ])
      
      const geo = new THREE.BufferGeometry()
      geo.setAttribute('position', new THREE.BufferAttribute(linePositions, 3))
      return {
        geometry: geo,
        positions: positions
      }
    }, [offsetX, offsetY])
    
    const color = isHovered ? INVERTED_BRAND_COLORS.dark : BRAND_COLORS.dark
    
    return (
      <group>
        {lineGeometry.positions.map((pos, i) => (
          <mesh key={i} position={pos}>
            <boxGeometry args={[squareSize, squareSize, 0.05]} />
            <meshBasicMaterial color={color} />
          </mesh>
        ))}
        <line geometry={lineGeometry.geometry} renderOrder={10}>
          <lineBasicMaterial 
            color={color} 
            linewidth={2} 
            depthTest={false} 
            transparent={true} 
          />
        </line>
      </group>
    )
  }

const highResDollarPattern: number[][] = [
  [0, 0, 0, 0, 1, 0, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 0, 0, 0],
  [0, 0, 1, 0, 1, 0, 1, 0, 0],
  [0, 0, 1, 0, 1, 0, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 0, 0, 0],
  [0, 0, 0, 0, 1, 0, 1, 0, 0],
  [0, 0, 1, 0, 1, 0, 1, 0, 0],
  [0, 0, 0, 1, 1, 1, 0, 0, 0],
  [0, 0, 0, 0, 1, 0, 0, 0, 0],
]

interface PixelDollarSignProps {
  isHovered?: boolean
}

const PixelDollarSign: React.FC<PixelDollarSignProps> = ({ isHovered = false }) => {
  return (
    <group>
      <PixelArt 
        pattern={highResDollarPattern} 
        pixelSize={0.05} 
        color={isHovered ? INVERTED_BRAND_COLORS.dark : BRAND_COLORS.dark}
        isHovered={isHovered}
      />
    </group>
  )
}


const NetworkVisualization: React.FC = () => {
  const startX = -6.0;
  const startY = 0;
  const startZ = 0;
  
  const xStep = 2;
  const yStep = 0.0
  const zStep = 2;
  
  const tabletCount = 4;
  const [hoveredTabletIndex, setHoveredTabletIndex] = useState<number | null>(null);
  
  const tabletPositions = Array.from({ length: tabletCount }, (_, i) => ({
    x: startX + (xStep * i),
    y: startY + (yStep * i), 
    z: startZ + (zStep * i)
  }));
  
  const tabletContent = [
    <PixelDollarSign isHovered={hoveredTabletIndex === 0} />,
    <CustomMagnifyingGlass isHovered={hoveredTabletIndex === 1} />,
    <PixelCandlestickChart isHovered={hoveredTabletIndex === 2} />,
    <PixelBinaryNumber binaryString="1000101" isHovered={hoveredTabletIndex === 3} />
  ];

  const offsetZ = 0

  return (
    <group position={[0, -3, offsetZ]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[40, 40]} />
        <meshBasicMaterial color="#202020" side={THREE.DoubleSide} />
      </mesh>

      {tabletPositions.map((pos, i) => {
        
        return (
          <Tablet
            key={`tablet-${i}`}
            position={[pos.x, pos.y, pos.z]}
            width={2}
            thickness={0.2}
            screenDepth={1}
            color={BRAND_COLORS.light}
            hoverColor={INVERTED_BRAND_COLORS.light}
            index={i}
            hoveredIndex={hoveredTabletIndex}
            onHover={setHoveredTabletIndex}
          >
            {tabletContent[i]}
          </Tablet>
        )
      })}
    </group>
  )
}

export default NetworkVisualization